import React, { useState, useEffect } from 'react';
import { database } from '../firebaseConfig';
import { ref, onValue } from 'firebase/database';
import { getFunctions } from 'firebase/functions';
import './ScheduledEmails.css'; // Verwende die eigene CSS-Datei

const ScheduledEmails = ({ userId }) => {
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState({});
  const [expandedEmails, setExpandedEmails] = useState({});

  const functions = getFunctions();

  // Überprüfe, ob Firebase Functions verfügbar sind
  useEffect(() => {
    if (!functions) {
      console.error('❌ Firebase Functions not available');
      setError('Firebase Functions not available. Please check your connection.');
      setLoading(false);
      return;
    }
    
    console.log('✅ Firebase Functions initialized successfully');
  }, [functions]);

  // Lade geplante E-Mails aus der Datenbank
  useEffect(() => {
    if (!userId) return;

    console.log('📅 Loading scheduled emails from database...');
    
    const scheduledEmailsRef = ref(database, `/World/${userId}/scheduledEmails`);
    
    const unsubscribe = onValue(scheduledEmailsRef, (snapshot) => {
      if (snapshot.exists()) {
        const scheduledEmailsData = snapshot.val();
        
        // Konvertiere in Array und berechne verbleibende Zeit
        const scheduledEmailsArray = Object.entries(scheduledEmailsData).map(([id, email]) => ({
          id,
          ...email,
          // Berechne verbleibende Zeit
          remainingTimeMs: Math.max(0, email.timestamp - Date.now()),
          remainingTimeMinutes: Math.max(0, Math.ceil((email.timestamp - Date.now()) / (1000 * 60))),
          // Status-Informationen
          isOverdue: email.timestamp < Date.now(),
          canCancel: email.timestamp > Date.now()
        })).sort((a, b) => a.timestamp - b.timestamp); // Sortiere nach Zeitstempel (nächste zuerst)
        
        setScheduledEmails(scheduledEmailsArray);
        console.log(`📅 ${scheduledEmailsArray.length} scheduled emails loaded`);
      } else {
        setScheduledEmails([]);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userId]);

  // Funktion zum Stornieren einer geplanten E-Mail
  const handleCancelEmail = async (scheduledEmailId) => {
    if (!scheduledEmailId) {
      console.error('❌ No scheduledEmailId provided');
      return;
    }
    
    console.log(`🚫 Starting cancel process for email: ${scheduledEmailId}`);
    console.log(`📋 User ID: ${userId}`);
    
    setCancelling(prev => ({ ...prev, [scheduledEmailId]: true }));
    
    try {
      console.log(`🔄 Calling cancelScheduledEmail function...`);
      
      // Direkter HTTP-Request zur Firebase Function
      const response = await fetch('https://us-central1-bonusclub-ab5e2.cloudfunctions.net/cancelScheduledEmail', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          scheduledEmailId
        })
      });
      
      console.log(`📨 Function response status:`, response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log(`📨 Function response data:`, result);
      
      if (result.success) {
        console.log(`✅ Successfully cancelled scheduled email: ${scheduledEmailId}`);
        console.log(`📧 Cancelled email details:`, {
          senderEmail: result.senderEmail,
          recipientEmail: result.recipientEmail,
          taskDeleted: result.taskDeleted,
          originalScheduledTime: result.originalScheduledTime
        });
        // Die E-Mail wird automatisch aus der Liste entfernt durch den onValue Listener
      } else {
        console.error(`❌ Function returned unsuccessful result:`, result);
        throw new Error(result.details || result.error || 'Failed to cancel email');
      }
    } catch (error) {
      console.error(`❌ Error cancelling scheduled email:`, error);
      console.error(`❌ Error details:`, {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      
      // Verbesserte Fehlermeldung basierend auf dem Fehlertyp
      let errorMessage = 'Error cancelling email';
      if (error.message.includes('404')) {
        errorMessage = 'Email not found - it may have already been sent or cancelled';
      } else if (error.message.includes('403')) {
        errorMessage = 'Permission denied - you may not have access to cancel this email';
      } else if (error.message.includes('CORS')) {
        errorMessage = 'Network error - please try again';
      } else {
        errorMessage = `Error: ${error.message}`;
      }
      
      setError(errorMessage);
      setTimeout(() => setError(null), 5000);
    } finally {
      setCancelling(prev => ({ ...prev, [scheduledEmailId]: false }));
    }
  };

  // Funktion zum Formatieren der verbleibenden Zeit
  const formatRemainingTime = (remainingTimeMs) => {
    if (remainingTimeMs <= 0) return 'Overdue';
    
    const minutes = Math.ceil(remainingTimeMs / (1000 * 60));
    
    if (minutes < 60) {
      return `${minutes}m`;
    } else if (minutes < 1440) { // weniger als 24 Stunden
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
    } else {
      const days = Math.floor(minutes / 1440);
      const remainingHours = Math.floor((minutes % 1440) / 60);
      return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
    }
  };

  // Funktion zum Formatieren des geplanten Zeitpunkts
  const formatScheduledTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    
    // Prüfe, ob es heute ist
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return `Today ${date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleString('de-DE', { 
        month: 'short', 
        day: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
  };

  // Funktion zum Extrahieren des Namens aus der E-Mail-Adresse
  const extractNameFromEmail = (email) => {
    if (!email) return 'Unknown';
    return email.split('@')[0];
  };

  // NEU: Funktion zum Togglen der E-Mail-Erweiterung
  const toggleEmailExpansion = (emailId) => {
    setExpandedEmails(prev => ({
      ...prev,
      [emailId]: !prev[emailId]
    }));
  };

  if (loading) {
    return (
      <div className="scheduledEmails_loading">
        <img src="/img/salesgod_dark.webp" alt="SalesGod" className="scheduledEmails_loading_spinner" />
        <p>Loading scheduled emails...</p>
      </div>
    );
  }

  if (scheduledEmails.length === 0) {
    return (
      <div className="scheduledEmails_empty">
        <div className="scheduledEmails_empty_illustration">
          <img 
            src="/img/clock-schedule.svg" 
            alt="No Scheduled Emails" 
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/img/mailerle.webp";
            }}
            style={{ maxWidth: "80px", maxHeight: "80px" }}
          />
        </div>
        <h3 className="scheduledEmails_empty_title">No scheduled emails</h3>
        <p className="scheduledEmails_empty_description">
          When you schedule emails for later delivery, they will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="scheduledEmails_container">
      {/* Header mit Statistiken */}
      <div className="scheduledEmails_header">
        <div className="scheduledEmails_stats">
          <div className="scheduledEmails_stat_item">
            <span className="scheduledEmails_stat_number">{scheduledEmails.length}</span>
            <span className="scheduledEmails_stat_label">Scheduled Emails</span>
          </div>
          
          {scheduledEmails.length > 0 && (
            <>
              <div className="scheduledEmails_stat_item">
                <span className="scheduledEmails_stat_number">
                  {scheduledEmails.filter(email => email.canCancel).length}
                </span>
                <span className="scheduledEmails_stat_label">Cancellable</span>
              </div>
              
              <div className="scheduledEmails_stat_item">
                <span className="scheduledEmails_stat_number">
                  {scheduledEmails.filter(email => email.isOverdue).length}
                </span>
                <span className="scheduledEmails_stat_label">Overdue</span>
              </div>
              
              <div className="scheduledEmails_stat_item">
                <span className="scheduledEmails_stat_number">
                  {scheduledEmails.filter(email => !email.isOverdue && email.canCancel).length}
                </span>
                <span className="scheduledEmails_stat_label">Pending</span>
              </div>
            </>
          )}
        </div>
        
        {scheduledEmails.length > 0 && (
          <div className="scheduledEmails_next_email">
            <span className="scheduledEmails_next_label">Next email in:</span>
            <span className="scheduledEmails_next_time">
              {formatRemainingTime(Math.min(...scheduledEmails.map(email => email.remainingTimeMs)))}
            </span>
          </div>
        )}
      </div>

      {error && (
        <div className="scheduledEmails_error">
          <p>{error}</p>
          <button onClick={() => setError(null)}>Close</button>
        </div>
      )}
      
      {scheduledEmails.map((email) => (
        <div key={email.id} className={`scheduledEmails_email_card ${email.isOverdue ? 'overdue' : ''} ${expandedEmails[email.id] ? 'expanded' : ''}`}>
          <div 
            className="scheduledEmails_email_header"
            onClick={() => toggleEmailExpansion(email.id)}
            style={{ cursor: 'pointer' }}
          >
            <div className="scheduledEmails_email_left">
              <div className="scheduledEmails_direction_label">
                Scheduled to:
              </div>
              <div className="scheduledEmails_recipient_info">
                <span className="scheduledEmails_recipient_name">
                  {extractNameFromEmail(email.recipientEmail)}
                </span>
                
                {/* E-Mail-Typ Badge */}
                {email.emailType && (
                  <span className={`scheduledEmails_type_badge ${email.emailType}`}>
                    {email.emailType === 'intro' && 'Introduction'}
                    {email.emailType === 'follow_up_1' && 'Follow-up 1'}
                    {email.emailType === 'follow_up_2' && 'Follow-up 2'}
                    {email.emailType === 'follow_up_3' && 'Follow-up 3'}
                    {email.emailType === 'email_reply' && 'Reply'}
                    {!['intro', 'follow_up_1', 'follow_up_2', 'follow_up_3', 'email_reply'].includes(email.emailType) && email.emailType}
                  </span>
                )}
                
                {/* Reply Badge */}
                {email.isReply && (
                  <span className="scheduledEmails_reply_badge">
                    Reply
                  </span>
                )}
              </div>
              
              <div className="scheduledEmails_subject">
                {email.subject}
              </div>
              
              <div className="scheduledEmails_recipient_email">
                {email.recipientEmail}
              </div>
            </div>
            
            <div className="scheduledEmails_email_right">
              <div className="scheduledEmails_time_info">
                <div className="scheduledEmails_scheduled_time">
                  📅 {formatScheduledTime(email.timestamp)}
                </div>
                <div className={`scheduledEmails_remaining_time ${email.isOverdue ? 'overdue' : ''}`}>
                  {formatRemainingTime(email.remainingTimeMs)}
                </div>
              </div>
              
              {/* Status Badge */}
              <div className="scheduledEmails_status_container">
                <span className={`scheduledEmails_status_badge ${email.isOverdue ? 'overdue' : 'scheduled'}`}>
                  {email.isOverdue ? 'Overdue' : 'Scheduled'}
                </span>
              </div>
              
              {/* Expand/Collapse Indicator */}
              <div className="scheduledEmails_expand_indicator">
                {expandedEmails[email.id] ? '▼' : '▶'}
              </div>
            </div>
          </div>

          {/* Erweiterte Details - nur anzeigen wenn erweitert */}
          {expandedEmails[email.id] && (
            <div className="scheduledEmails_expanded_content">
              {/* Zeige den vollständigen Inhalt mit HTML-Formatierung */}
              <div className="scheduledEmails_preview">
                {email.content ? (
                  <div 
                    dangerouslySetInnerHTML={{ 
                      __html: email.content 
                    }}
                    style={{
                      maxHeight: '400px',
                      overflowY: 'auto',
                      lineHeight: '1.6',
                      whiteSpace: 'pre-wrap'
                    }}
                  />
                ) : (
                  'No preview available'
                )}
              </div>
              
              {/* Zusätzliche Informationen */}
              <div className="scheduledEmails_details">
                <div className="scheduledEmails_detail_item">
                  <span className="scheduledEmails_detail_label">From:</span>
                  <span className="scheduledEmails_detail_value">{email.senderEmail}</span>
                </div>
                
                {email.businessName && (
                  <div className="scheduledEmails_detail_item">
                    <span className="scheduledEmails_detail_label">Business:</span>
                    <span className="scheduledEmails_detail_value">{email.businessName}</span>
                  </div>
                )}
                
                {email.randomFactor && (
                  <div className="scheduledEmails_detail_item">
                    <span className="scheduledEmails_detail_label">Delay Factor:</span>
                    <span className="scheduledEmails_detail_value">
                      {(email.randomFactor * 100).toFixed(0)}% ({email.randomizedQueueDuration}min)
                    </span>
                  </div>
                )}
                
                {email.sleepTimeAdjusted && (
                  <div className="scheduledEmails_detail_item">
                    <span className="scheduledEmails_detail_label">Sleep Time:</span>
                    <span className="scheduledEmails_detail_value">
                      Adjusted ({email.sleepTimeStart} - {email.sleepTimeEnd})
                    </span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="scheduledEmails_actions">
                {email.canCancel && (
                  <button 
                    className="scheduledEmails_cancel_button"
                    onClick={(e) => {
                      e.stopPropagation(); // Verhindere das Togglen beim Klick auf den Button
                      handleCancelEmail(email.id);
                    }}
                    disabled={cancelling[email.id]}
                    title="Cancel scheduled email"
                  >
                    {cancelling[email.id] ? (
                      <>
                        <span className="scheduledEmails_spinner"></span>
                        Cancelling...
                      </>
                    ) : (
                      <>
                        🚫 Cancel
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ScheduledEmails; 